import React, { useEffect, useRef, useState } from 'react';
import { Check, ImagePlus, Loader2, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import OptionSheet from './OptionSheet';
import { supabase } from '../../lib/supabase';
import { isHeicFile, convertHeicToJpeg } from '../../utils/heicConvert';

type SavedScene = { id: string; nombre: string | null; nombre_archivo: string; signedUrl?: string };

interface EscenasGuardadasOptionProps {
  open: boolean;
  onClose: () => void;
  selectedSceneId: string | null;
  onSelect: (id: string | null, nombreArchivo?: string, signedUrl?: string, nombre?: string) => void;
}

const EscenasGuardadasOption: React.FC<EscenasGuardadasOptionProps> = ({
  open,
  onClose,
  selectedSceneId,
  onSelect,
}) => {
  const [scenes, setScenes] = useState<SavedScene[]>([]);
  const [loading, setLoading] = useState(false);
  const [localSelectedId, setLocalSelectedId] = useState<string | null>(selectedSceneId);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [deletingSceneId, setDeletingSceneId] = useState<string | null>(null);

  // Add scene form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState('');
  const [addFile, setAddFile] = useState<File | null>(null);
  const [addPreviewUrl, setAddPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [convertingHeic, setConvertingHeic] = useState(false);

  const snapshotRef = useRef<string | null>(null);

  useEffect(() => {
    if (open) {
      snapshotRef.current = selectedSceneId;
      setLocalSelectedId(selectedSceneId);
      if (scenes.length === 0 && !loading) fetchScenes();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetAddState = () => {
    setAddName('');
    setAddFile(null);
    setAddPreviewUrl(null);
    setSaving(false);
    setAddError(null);
  };

  const fetchScenes = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) return;
      const { data, error } = await supabase.from('fondos_personalizados').select('id, nombre, nombre_archivo').eq('id_company', idCompany).order('created_at', { ascending: false });
      if (error) throw error;
      const rows = (data as any[]) || [];
      const withSigned = await Promise.all(
        rows.map(async (s) => {
          let signedUrl: string | undefined;
          const fileName = s?.nombre_archivo;
          if (fileName) {
            try {
              const { data: fullSigned } = await supabase.storage.from('fondos_personalizados').createSignedUrl(`${idCompany}/full/${fileName}`, 3600);
              if (fullSigned?.signedUrl) signedUrl = fullSigned.signedUrl;
            } catch {}
          }
          return { id: s.id as string, nombre: s.nombre as string | null, nombre_archivo: s.nombre_archivo as string, signedUrl };
        }),
      );
      setScenes(withSigned);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (file: File) => {
    if (isHeicFile(file)) {
      try {
        setConvertingHeic(true);
        const { file: converted, url } = await convertHeicToJpeg(file, 0.85);
        setAddFile(converted);
        setAddPreviewUrl(url);
        setAddError(null);
      } catch {
        setAddError('No pudimos procesar la imagen. Probá con una imagen en formato PNG o JPG.');
      } finally {
        setConvertingHeic(false);
      }
    } else {
      setAddFile(file);
      setAddPreviewUrl(URL.createObjectURL(file));
      setAddError(null);
    }
  };

  const handleSave = async () => {
    try {
      setAddError(null);
      if (!addFile) throw new Error('Subí una imagen de la escena');
      setSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No se encontró el usuario');
      const { data: profile } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) throw new Error('No se encontró la empresa');
      const ext = (addFile.name.split('.').pop() || 'jpg').toLowerCase();
      const base = addName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      const fileName = `${Date.now()}-${base || 'escena'}.${ext}`;
      const targetPath = `${idCompany}/full/${fileName}`;
      const { error: upErr } = await supabase.storage.from('fondos_personalizados').upload(targetPath, addFile, { cacheControl: '3600', upsert: false, contentType: addFile.type || 'image/jpeg' });
      if (upErr) throw upErr;
      const { error: insErr } = await supabase.from('fondos_personalizados').insert({ id_company: idCompany, nombre: addName.trim() || null, nombre_archivo: fileName });
      if (insErr) throw insErr;
      await fetchScenes();
      setShowAddForm(false);
      resetAddState();
    } catch (e: any) {
      console.error('Error subiendo escena:', e);
      setAddError(e?.message || 'No se pudo guardar la escena');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sceneId: string) => {
    const scene = scenes.find((s) => s.id === sceneId);
    if (!scene) return;
    setOpenMenuId(null);
    setDeletingSceneId(sceneId);
    // Wait for fade-out animation
    await new Promise(r => setTimeout(r, 300));
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase.from('profiles').select('id_company').eq('id', user.id).single();
      const idCompany = (profile as any)?.id_company;
      if (!idCompany) return;
      await supabase.storage.from('fondos_personalizados').remove([`${idCompany}/full/${scene.nombre_archivo}`]);
      await supabase.from('fondos_personalizados').delete().eq('id', sceneId);
      if (localSelectedId === sceneId) setLocalSelectedId(null);
      setScenes((prev) => prev.filter((s) => s.id !== sceneId));
    } catch (e) {
      console.error('Error deleting scene:', e);
    }
    setDeletingSceneId(null);
  };

  const handleClose = () => {
    setLocalSelectedId(snapshotRef.current);
    setShowAddForm(false);
    resetAddState();
    onClose();
  };

  const handleAccept = () => {
    const scene = scenes.find((s) => s.id === localSelectedId);
    onSelect(localSelectedId, scene?.nombre_archivo, scene?.signedUrl, scene?.nombre || undefined);
    setShowAddForm(false);
    resetAddState();
  };

  // Add scene form content
  const addFormContent = (
    <div className="space-y-4">
      <div>
        <label className="block text-[15px] font-medium text-neutral-700 mb-1">Nombre de la escena</label>
        <input type="text" value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Ej: Playa, Estudio, Jardín..." className="w-full rounded-md border border-neutral-200 p-3 text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-primary" />
      </div>
      <div>
        <label className="block text-[15px] font-medium text-neutral-700 mb-1">Imagen de la escena</label>
        {convertingHeic && !addFile && (
          <div className="bg-surface-card border border-neutral-200 rounded-lg p-4 mb-3 flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
            <p className="text-[15px] text-neutral-600">Subiendo imagen...</p>
          </div>
        )}
        {addFile && (
          <div className="bg-surface-card border border-neutral-200 rounded-lg p-3 mb-3">
            <div className="flex items-center gap-3">
              {addPreviewUrl ? (
                <img src={addPreviewUrl} alt="Preview" className="w-20 h-20 object-cover rounded-md shrink-0" />
              ) : (
                <div className="w-20 h-20 rounded-md shrink-0 bg-neutral-100 flex items-center justify-center">
                  <span className="material-symbols-rounded text-[24px] text-neutral-400">image</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[15px] text-neutral-700 truncate">{addFile.name}</p>
                <button type="button" onClick={() => { setAddFile(null); setAddPreviewUrl(null); }} className="text-[13px] text-error hover:opacity-70 mt-1">Eliminar</button>
              </div>
            </div>
          </div>
        )}
        <input type="file" id="add-scene-file-comp" accept="image/*,.heic,.heif" onChange={(e) => { const file = e.target.files?.[0]; if (file) handleFileSelect(file); e.target.value = ''; }} className="hidden" />
        <label
          htmlFor="add-scene-file-comp"
          className={`inline-flex w-full justify-center items-center h-10 px-4 shadow-sm text-[15px] font-medium rounded-md cursor-pointer transition-colors ${
            convertingHeic ? 'pointer-events-none opacity-50 border border-neutral-200 text-neutral-900 bg-surface-card' : !addFile ? 'bg-brand-primary text-white hover:bg-brand-primary-hover' : 'border border-neutral-200 text-neutral-900 bg-surface-card hover:bg-neutral-100'
          }`}
        >
          <ImagePlus className="w-4 h-4 mr-2" />
          {convertingHeic ? 'Subiendo...' : !addFile ? 'Subir imagen' : 'Cambiar imagen'}
        </label>
      </div>
      {addError && <p className="text-[15px] text-error">{addError}</p>}
    </div>
  );

  // List content
  const listContent = (
    <div className="flex flex-col h-full">
      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      )}
      {!loading && scenes.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <span className="material-symbols-rounded text-[40px] text-neutral-300">bookmark</span>
          <p className="text-[15px] text-neutral-500 text-center">No tenés escenas guardadas todavía.</p>
          <button type="button" onClick={() => setShowAddForm(true)} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-md bg-brand-primary text-white text-[15px] font-medium hover:bg-brand-primary-hover shadow-sm">
            <ImagePlus className="w-4 h-4" />
            Añadir escena
          </button>
        </div>
      )}
      {!loading && scenes.length > 0 && (
        <div className="grid grid-cols-2 gap-3 overflow-y-auto flex-1 content-start">
          <button type="button" onClick={() => setShowAddForm(true)} className="relative rounded-md border border-neutral-200 overflow-hidden transition-colors hover:border-neutral-300 group bg-white">
            <div className="w-full h-24 bg-white flex items-center justify-center">
              <div className="w-12 h-12 rounded-full bg-brand-primary text-white flex items-center justify-center transition-colors group-hover:bg-brand-primary-hover">
                <Plus className="w-6 h-6" />
              </div>
            </div>
            <p className="text-[15px] text-neutral-900 p-2 font-medium">Añadir escena</p>
          </button>
          {scenes.map((scene) => {
            const isSelected = localSelectedId === scene.id;
            return (
              <div
                key={scene.id}
                onClick={() => {
                  if (openMenuId !== null) { setOpenMenuId(null); return; }
                  setLocalSelectedId(isSelected ? null : scene.id);
                }}
                className={`relative w-full h-fit text-left border rounded-md p-2 cursor-pointer transition-all duration-300 ${
                  deletingSceneId === scene.id ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
                } ${
                  openMenuId === scene.id ? 'z-30' : ''
                } ${
                  isSelected ? 'border-brand-primary ring-2 ring-brand-primary' : 'border-neutral-200 bg-white hover:bg-neutral-50'
                }`}
              >
                <div className="relative overflow-hidden rounded-sm h-24 bg-neutral-100">
                  {scene.signedUrl ? (
                    <img src={scene.signedUrl} alt={scene.nombre || 'Escena'} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-rounded text-[24px] text-neutral-300">image</span>
                    </div>
                  )}
                  {isSelected && (
                    <span className="absolute top-2 right-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-primary text-white">
                      <Check className="w-3 h-3" />
                    </span>
                  )}
                </div>
                <div className="flex items-center mt-2">
                  <p className="text-[13px] text-neutral-700 truncate flex-1">{scene.nombre || 'Escena'}</p>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === scene.id ? null : scene.id); }}
                    className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full hover:bg-neutral-100 transition-colors"
                  >
                    <MoreHorizontal className="w-3.5 h-3.5 text-neutral-400" />
                  </button>
                </div>
                {openMenuId === scene.id && (
                  <div
                    className="absolute bottom-0 right-2 translate-y-full z-20 mt-1 bg-white rounded-md shadow-lg border border-neutral-200 py-1 min-w-[140px]"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchEnd={(e) => e.stopPropagation()}
                  >
                    <button type="button" onClick={() => handleDelete(scene.id)} className="w-full text-left px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2">
                      <Trash2 className="w-3.5 h-3.5" />
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  let sheetTitle = 'Escenas guardadas';
  let sheetContent = listContent;
  let sheetAcceptDisabled = !localSelectedId;
  let sheetOnAccept: (() => void) | undefined = handleAccept;
  let sheetOnClose = handleClose;

  if (showAddForm) {
    sheetTitle = 'Añadir escena';
    sheetContent = addFormContent;
    sheetAcceptDisabled = saving || !addFile;
    sheetOnAccept = handleSave;
    sheetOnClose = () => { setShowAddForm(false); resetAddState(); };
  }

  return (
    <OptionSheet
      open={open}
      onClose={sheetOnClose}
      onAccept={sheetOnAccept}
      title={sheetTitle}
      acceptDisabled={sheetAcceptDisabled}
      acceptLabel={showAddForm ? (saving ? 'Guardando...' : 'Guardar escena') : 'Aceptar'}
      closeOnAccept={false}
    >
      {sheetContent}
    </OptionSheet>
  );
};

export default EscenasGuardadasOption;
